import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TreeSelectProps, Id, TreeNode, Sentiment } from './types'
import { saveSelectionMulti, loadChildren } from './api'
import { useTreeData, idKey } from './useTreeData'
import SentimentIndicator from './components/SentimentIndicator'
import SentimentTooltip from './components/SentimentTooltip'

const MultiTreeSelect: React.FC<TreeSelectProps> = (props) => {
	const {
		endpoint,
		valueKey,
		isSearchable,
		selectParents,
		defaultValue,
		defaultTitle,
		placeholder,
		inputId,
		preloadedData,
		preloadedActive,
		autoOpen = false,
		withSentiment = false,
		questionId = null,
		initialSentiments = null,
		onChange = undefined,
		translations = null,
	} = props
	const backend = useMemo(() => ({ endpoint, valueKey }), [endpoint, valueKey])

	const {
		root,
		setRoot,
		nodes,
		indexNodes,
		expandedIds,
		setExpandedIds,
		loadingIds,
		ensureChildrenLoaded,
		pending,
		setPending,
		search,
		setSearch,
	} = useTreeData({
		backend,
		isSearchable,
		preloadedData,
		preloadedActive,
		defaultValue,
		defaultTitle,
	})

	const [open, setOpen] = useState<boolean>(false)
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
	const [containerStyle, setContainerStyle] = useState<React.CSSProperties>({})
	const containerRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLDivElement | null>(null)

	const [selected, setSelected] = useState<Array<Id>>(
		Array.isArray(defaultValue)
			? defaultValue
			: defaultValue !== null && defaultValue !== undefined
				? [defaultValue]
				: [],
	)
	const [titles, setTitles] = useState<Array<string>>(
		Array.isArray(defaultTitle) ? defaultTitle : defaultTitle ? [defaultTitle] : [],
	)

	const [stagedSelected, setStagedSelected] = useState<Array<Id>>(selected)
	const [stagedTitles, setStagedTitles] = useState<Array<string>>(titles)
	const [isDirty, setIsDirty] = useState<boolean>(false)

	// Sentiment state: map category ID to sentiment value
	const [sentiments, setSentiments] = useState<Record<string, Sentiment>>(() => {
		const initial: Record<string, Sentiment> = {}
		if (withSentiment) {
			// 1. Initialize from initialSentiments prop
			if (initialSentiments) {
				Object.entries(initialSentiments).forEach(([key, value]) => {
					initial[idKey(key)] = value
				})
			}
			// 2. Supplement from preloaded nodes if any
			if (nodes) {
				Object.values(nodes).forEach((node) => {
					if (node.sentiment !== undefined) {
						initial[idKey(node.id)] = node.sentiment
					}
				})
			}
		}
		return initial
	})

	// Update container and dropdown position relative to the viewport
	useLayoutEffect(() => {
		if (!open || !containerRef.current) {
			setContainerStyle({})
			setDropdownStyle({})
			return
		}

		const updatePosition = () => {
			if (!containerRef.current) return
			// We use parent element (the mount point) to get the original position
			const mountRect = containerRef.current.parentElement?.getBoundingClientRect()
			if (!mountRect) return

			const winHeight = window.innerHeight
			const winWidth = window.innerWidth
			const targetWidth = 1400
			const width = Math.min(targetWidth, winWidth * 0.95)
			const dropdownHeight = 450 // Max height from CSS

			// Calculate container position (the select bar)
			let containerLeft = mountRect.left - (width - mountRect.width) / 2
			if (containerLeft < 10) containerLeft = 10
			if (containerLeft + width > winWidth - 10) containerLeft = winWidth - width - 10

			setContainerStyle({
				position: 'fixed',
				top: `${mountRect.top}px`,
				left: `${containerLeft}px`,
				width: `${width}px`,
				zIndex: 10000,
				background: 'white',
				borderRadius: '8px',
				boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
			})

			// Calculate dropdown position relative to the container select bar
			const currentTriggerRect = triggerRef.current?.getBoundingClientRect()
			if (currentTriggerRect) {
				let dropdownTop = currentTriggerRect.bottom

				// If it goes off screen at the bottom, show it above the trigger
				if (dropdownTop + dropdownHeight > winHeight && currentTriggerRect.top > dropdownHeight) {
					dropdownTop = currentTriggerRect.top - dropdownHeight
				}

				setDropdownStyle({
					position: 'fixed',
					top: `${dropdownTop}px`,
					left: `${containerLeft}px`,
					width: `${width}px`,
					zIndex: 10001,
				})
			}
		}

		updatePosition()
		// Re-run after a frame to ensure triggerRef has updated height if more bubbles appeared
		const raf = requestAnimationFrame(updatePosition)

		window.addEventListener('scroll', updatePosition)
		window.addEventListener('resize', updatePosition)
		return () => {
			cancelAnimationFrame(raf)
			window.removeEventListener('scroll', updatePosition)
			window.removeEventListener('resize', updatePosition)
		}
	}, [open, stagedTitles.length])

	// Sync hidden input with committed selection
	useEffect(() => {
		const input = document.getElementById(inputId) as HTMLInputElement | null
		if (!input) return
		input.value = selected.join(',')
	}, [inputId, selected])

	// Sync categories_${questionId} hidden input for sentiment-enabled forms
	useEffect(() => {
		if (!withSentiment || questionId === null) return

		const input = document.getElementById(`categories_${questionId}`) as HTMLInputElement | null
		if (!input) return

		// Build array of {value: categoryId, sentiment: sentimentValue}
		const data = selected.map((id) => {
			const key = idKey(id)
			// Default to 0 (neutral) if sentiment is not set or is null
			const sentimentValue = sentiments[key]
			const sentiment = sentimentValue !== undefined && sentimentValue !== null ? sentimentValue : 0
			return {
				value: id,
				sentiment: sentiment,
			}
		})
		// Save as JSON
		input.value = JSON.stringify(data)
	}, [questionId, selected, sentiments, withSentiment])

	// Update sentiments from nodes when they're loaded
	useEffect(() => {
		if (!withSentiment) return
		const newSentiments: Record<string, Sentiment> = { ...sentiments }
		let hasChanges = false

		Object.values(nodes).forEach((node) => {
			const key = idKey(node.id)
			if (node.sentiment !== undefined && sentiments[key] === undefined) {
				newSentiments[key] = node.sentiment
				hasChanges = true
			}
		})

		if (hasChanges) {
			setSentiments(newSentiments)
		}
	}, [nodes, withSentiment])

	// Call onChange callback when selected categories or sentiments change
	useEffect(() => {
		if (!onChange) return

		// Build category data for selected items AND their parents
		const categories: Record<
			string,
			{
				name: string
				parentId: Id | null
				sentiment: Sentiment
			}
		> = {}

		// Collect all parent IDs needed
		const parentIds = new Set<string>()

		selected.forEach((id) => {
			const key = idKey(id)
			const node = nodes[key]

			if (node) {
				// Add selected category
				categories[key] = {
					name: node.title,
					parentId: node.parentId ?? null,
					sentiment: sentiments[key] ?? 0,
				}

				// Collect parent hierarchy
				let currentParentId = node.parentId
				const guard = new Set<string>()

				while (currentParentId !== null && currentParentId !== undefined) {
					const parentKey = idKey(currentParentId)
					if (guard.has(parentKey)) break // Prevent infinite loop
					guard.add(parentKey)
					parentIds.add(parentKey)

					const parentNode = nodes[parentKey]
					if (parentNode) {
						currentParentId = parentNode.parentId
					} else {
						break
					}
				}
			}
		})

		// Add parent nodes to categories
		parentIds.forEach((parentKey) => {
			const parentNode = nodes[parentKey]
			if (parentNode && !categories[parentKey]) {
				categories[parentKey] = {
					name: parentNode.title,
					parentId: parentNode.parentId ?? null,
					sentiment: sentiments[parentKey] ?? null, // Parents typically don't have sentiment
				}
			}
		})

		onChange({
			selectedIds: selected,
			categories,
		})
	}, [selected, sentiments, nodes, onChange])

	// Auto-open on mount if enabled
	useEffect(() => {
		if (autoOpen) {
			// Small delay to ensure component is fully rendered
			const timer = setTimeout(() => setOpen(true), 100)
			return () => clearTimeout(timer)
		}
	}, [autoOpen])

	// When opening, mirror committed into staged
	useEffect(() => {
		if (!open) return
		setStagedSelected(selected)
		setStagedTitles(titles)
		setIsDirty(false)
	}, [open])

	const isSelected = useCallback(
		(id: Id): boolean => {
			const source = open ? stagedSelected : selected
			const explicit = source.some((v) => idKey(v) === idKey(id))
			if (explicit) return true
			if (!selectParents) return false
			// derived selection for parents
			for (const v of source) {
				let cur = nodes[idKey(v)]
				const guard = new Set<string>()
				while (cur && cur.parentId !== null && cur.parentId !== undefined) {
					const pk = idKey(cur.parentId)
					if (pk === idKey(id)) return true
					if (guard.has(pk)) break
					guard.add(pk)
					cur = nodes[pk]
				}
			}
			return false
		},
		[nodes, open, selectParents, selected, stagedSelected],
	)

	const collectParents = useCallback(
		(id: Id): Array<Id> => {
			const out: Array<Id> = []
			let cur = nodes[idKey(id)]
			const guard = new Set<string>()
			while (cur && cur.parentId !== null && cur.parentId !== undefined) {
				const pk = idKey(cur.parentId)
				if (guard.has(pk)) break
				guard.add(pk)
				out.push(cur.parentId)
				cur = nodes[pk]
			}
			return out
		},
		[nodes],
	)

	const commitIfDirty = useCallback(async () => {
		if (!isDirty) return

		// Normal tree select - use AJAX save (when not using questionId form submission)
		if (!withSentiment || questionId === null) {
			await saveSelectionMulti(backend, valueKey, stagedSelected, withSentiment ? sentiments : undefined)
		}

		setSelected(stagedSelected)
		setTitles(stagedTitles)
		setIsDirty(false)
	}, [backend, isDirty, stagedSelected, stagedTitles, valueKey, withSentiment, questionId, sentiments])

	const handleSentimentChange = useCallback(
		(nodeId: Id, newSentiment: Sentiment) => {
			const key = idKey(nodeId)
			setSentiments((prev) => ({
				...prev,
				[key]: newSentiment,
			}))
			// If not open, we should also update 'selected' to trigger the effect that updates the hidden input
			// although just updating 'sentiments' should be enough as it's in the dependency array of the effect.
			// However, if we are NOT open, we are effectively 'committed' already.
			if (!open) {
				setIsDirty(false) // Not dirty relative to staged, because we don't use staging when closed
				// If closed, we also update committed state immediately
				const existsInSelected = selected.some((v) => idKey(v) === idKey(nodeId))
				if (existsInSelected) {
					// Trigger effect that updates hidden input by slightly changing selection if needed,
					// or just rely on the effect that depends on 'sentiments'
					setSelected((prev) => [...prev])
				}
			} else {
				setIsDirty(true)
			}
		},
		[open, selected],
	)

	const onToggleExpand = useCallback(
		async (node: TreeNode) => {
			const k = idKey(node.id)
			const willExpand = !expandedIds.has(k)
			setExpandedIds((prev) => {
				const next = new Set(prev)
				next.has(k) ? next.delete(k) : next.add(k)
				return next
			})
			if (willExpand && node.hasChildren && !node.loaded) {
				await ensureChildrenLoaded(node)
			}
		},
		[expandedIds, ensureChildrenLoaded, setExpandedIds],
	)

	const onToggleCheck = useCallback(
		(node: TreeNode) => {
			const source = open ? stagedSelected : selected
			const exists = source.some((v) => idKey(v) === idKey(node.id))
			let next = exists ? source.filter((v) => idKey(v) !== idKey(node.id)) : [...source, node.id]
			if (selectParents && !exists) {
				const parents = collectParents(node.id)
				parents.forEach((p) => {
					if (!next.some((v) => idKey(v) === idKey(p))) next.push(p)
				})
			}

			// Initialize sentiment for newly selected categories
			if (withSentiment && !exists) {
				const key = idKey(node.id)
				if (sentiments[key] === undefined) {
					setSentiments((prev) => ({
						...prev,
						[key]: node.sentiment !== undefined ? node.sentiment : 0, // Default to neutral
					}))
				}
			}

			const nextTitles = next
				.map((id) => nodes[idKey(id)]?.title)
				.filter((t): t is string => typeof t === 'string' && t !== '')
			if (open) {
				setStagedSelected(next)
				setStagedTitles(nextTitles)
				setIsDirty(true)
			} else {
				setSelected(next)
				setTitles(nextTitles)
				if (!withSentiment || questionId === null) {
					void saveSelectionMulti(backend, valueKey, next, withSentiment ? sentiments : undefined)
				}
			}
		},
		[
			backend,
			collectParents,
			nodes,
			open,
			selectParents,
			selected,
			stagedSelected,
			valueKey,
			withSentiment,
			sentiments,
		],
	)

	// Titles shown in top bar should reflect staged values when open
	const headerTitles = open ? stagedTitles : titles

	// Load root data on open if not present and not searching
	useEffect(() => {
		let canceled = false
		if (!open) return
		if (root.length > 0 && search === '') return
		if (search !== '') return
		setPending(true)
		loadChildren(backend, undefined)
			.then((data) => {
				if (canceled) return
				indexNodes(data)
				setRoot(data)
				// Update sentiments from loaded data
				if (withSentiment) {
					const newSentiments: Record<string, Sentiment> = { ...sentiments }
					data.forEach((node) => {
						if (node.sentiment !== undefined) {
							newSentiments[idKey(node.id)] = node.sentiment
						}
					})
					setSentiments(newSentiments)
				}
			})
			.finally(() => setPending(false))
		return () => {
			canceled = true
		}
	}, [backend, indexNodes, open, root.length, search, setPending, setRoot, withSentiment])

	// Close on outside click
	useEffect(() => {
		if (!open) return
		const onDocMouseDown = (e: MouseEvent) => {
			const target = e.target as Node | null
			if (!containerRef.current) return
			if (target && containerRef.current.contains(target)) return
			// Check if target is inside the portal dropdown
			const portalDropdown = document.getElementById('tree-select-portal-dropdown-multi')
			if (target && portalDropdown?.contains(target)) return
			setOpen(false)
		}
		// Use capture phase to catch clicks even if propagation is stopped
		document.addEventListener('mousedown', onDocMouseDown, true)
		return () => document.removeEventListener('mousedown', onDocMouseDown, true)
	}, [open])

	const Item: React.FC<{ node: TreeNode; level: number }> = ({ node, level }) => {
		const expanded = expandedIds.has(idKey(node.id))
		const isLoading = loadingIds.has(idKey(node.id))
		const checked = isSelected(node.id)
		return (
			<div className="select-none">
				<div
					className="flex items-center gap-2 py-1.5 px-2 hover:bg-slate-50 rounded-md transition-colors"
					style={{ paddingLeft: `${level * 16 + 8}px` }}
					onClick={() => onToggleExpand(node)}
				>
					{node.hasChildren ? (
						<button
							type="button"
							className={`flex items-center justify-center w-5 h-5 text-slate-600 hover:text-slate-800 rounded transition-transform ${expanded ? 'rotate-90' : ''}`}
							onClick={(e) => {
								e.stopPropagation()
								void onToggleExpand(node)
							}}
							title={expanded ? 'Collapse' : 'Expand'}
						>
							<i className="fa-solid fa-chevron-right text-[10px]"></i>
						</button>
					) : (
						<span className="inline-block w-5" />
					)}
					<label
						className="relative inline-flex items-center select-none cursor-pointer"
						onClick={(e) => e.stopPropagation()}
					>
						<input type="checkbox" className="sr-only" checked={checked} onChange={() => onToggleCheck(node)} />
						<div
							className={`w-4 h-4 border-2 rounded transition-colors duration-150 flex items-center justify-center ${
								checked ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300 hover:border-gray-400'
							}`}
						>
							{checked ? <i className="fa-solid fa-check text-white text-[8px]"></i> : null}
						</div>
					</label>
					<span className={`truncate ${checked ? 'text-blue-600 font-medium' : ''}`} title={node.title}>
						{node.title}
					</span>
				</div>
				{expanded && isLoading && (
					<div className="pl-6 py-1 text-slate-500 text-xs" style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}>
						<i className="fa-solid fa-spinner fa-spin mr-2"></i>
						Loading...
					</div>
				)}
				{expanded && node.children && node.children.length > 0 && (
					<div className="ml-4">
						{node.children.map((c) => (
							<div key={idKey(c.id)} className="border-b border-gray-100">
								<Item node={c} level={level + 1} />
							</div>
						))}
					</div>
				)}
			</div>
		)
	}

	return (
		<div ref={containerRef} className={`p-2 js-tree-select-container ${open ? 'is-open' : ''}`} style={containerStyle}>
			<div className="relative min-h-10">
				<div ref={triggerRef} className="relative w-full min-h-10">
					{pending && (
						<div className="absolute inset-0 m-auto z-20 grid place-items-center bg-white/80 backdrop-blur-sm select-none pointer-events-none rounded-lg">
							<div className="flex items-center gap-2">
								<div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
								<span className="text-sm text-gray-600 font-medium">Loading...</span>
							</div>
						</div>
					)}

					<div
						onClick={() => setOpen((v) => !v)}
						className={`my-2 transition-all duration-200 bg-white border-2 w-full min-h-12 border-gray-200 hover:border-gray-300 pl-4 pr-12 py-3 text-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 text-left cursor-pointer rounded-lg shadow-sm hover:shadow-md ${open ? 'border-blue-500 ring-4 ring-blue-100 shadow-md' : ''}`}
						tabIndex={0}
					>
						<div className="flex flex-wrap gap-1.5 items-center min-h-6">
							{headerTitles.length === 0 ? (
								<span className="text-slate-500">{placeholder ?? 'Select items...'}</span>
							) : (
								headerTitles.map((t, idx) => {
									const currentIds = open ? stagedSelected : selected
									const nodeId = currentIds[idx]
									// Default to 0 (neutral) if sentiment is not set
									const sentiment = withSentiment && nodeId ? (sentiments[idKey(nodeId)] ?? 0) : undefined

									return (
										<div
											key={`${nodeId}-${idx}`}
											className="inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 px-3 py-1.5 rounded-lg text-sm font-medium border border-blue-200 shadow-sm hover:shadow-md transition-all duration-150 max-w-[600px]"
											onClick={(e) => e.stopPropagation()}
										>
											<span className="truncate">{t}</span>
											{withSentiment && nodeId && sentiment !== undefined && (
												<SentimentTooltip
													currentSentiment={sentiment}
													onSentimentChange={(newSentiment) => handleSentimentChange(nodeId, newSentiment)}
													translations={translations}
												>
													<div className="cursor-pointer flex items-center">
														<SentimentIndicator sentiment={sentiment} translations={translations} />
													</div>
												</SentimentTooltip>
											)}
											<button
												type="button"
												onClick={(e) => {
													e.stopPropagation()
													// remove this chip from committed state when closed, or staged when open
													if (open) {
														const next = stagedSelected.filter((_, i) => i !== idx)
														const nextTitles = stagedTitles.filter((_, i) => i !== idx)
														setStagedSelected(next)
														setStagedTitles(nextTitles)
														setIsDirty(true)

														// If we are in manual form submission mode (questionId !== null),
														// we should immediately update the committed state as well,
														// so the bubble doesn't reappear when closing the dropdown
														if (questionId !== null) {
															setSelected(next)
															setTitles(nextTitles)
														}
													} else {
														const next = selected.filter((_, i) => i !== idx)
														const nextTitles = titles.filter((_, i) => i !== idx)
														setSelected(next)
														setTitles(nextTitles)
														if (!withSentiment || questionId === null) {
															void saveSelectionMulti(backend, valueKey, next)
														}
													}
												}}
												className="ml-0.5 hover:bg-blue-200 rounded-full p-1 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
											>
												<i className="fa-solid fa-times text-[10px] text-blue-600"></i>
											</button>
										</div>
									)
								})
							)}
						</div>
					</div>
					<button
						type="button"
						className={`transition size-4 absolute z-10 top-3 right-2 my-auto flex items-center focus:outline-none ${open ? '-rotate-180' : ''}`}
						onClick={() => setOpen((v) => !v)}
					>
						<i className="block fa-solid fa-chevron-down text-xs text-slate-900"></i>
					</button>
				</div>
			</div>

			{open &&
				createPortal(
					<div
						id="tree-select-portal-dropdown-multi"
						className="js-tree-select-dropdown bg-white shadow-2xl max-h-[450px] border-2 border-gray-200 flex flex-col rounded-lg overflow-hidden"
						onClick={(e) => e.stopPropagation()}
						style={dropdownStyle}
					>
						<div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
							<span className="text-xs text-slate-600 font-medium">
								<span>{stagedSelected.length}</span> item{stagedSelected.length !== 1 ? 's' : ''} selected
							</span>
							{(stagedSelected.length > 0 || stagedTitles.length > 0) && (
								<button
									type="button"
									onClick={() => {
										setStagedSelected([])
										setStagedTitles([])
										setIsDirty(true)
										// When we clear, we should immediately update the committed state
										// if we are in a non-AJAX mode (questionId !== null) to ensure the hidden input is cleared
										if (questionId !== null) {
											setSelected([])
											setTitles([])
										}
									}}
									className="flex items-center gap-1.5 px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-20"
								>
									<i className="fa-solid fa-trash-can text-[10px]"></i>
									<span>Clear</span>
								</button>
							)}
						</div>

						{isSearchable && (
							<div className="flex items-center px-4 border-b border-slate-300 w-full">
								<div className="relative w-full">
									<input
										type="text"
										className="transition block bg-slate-100 border border-slate-300 w-full h-8 pl-2 pr-3 text-[10px] focus:border-slate-900 placeholder:text-slate-400"
										placeholder="Search..."
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
								</div>
							</div>
						)}

						<div className="overflow-x-hidden overflow-y-auto flex-1">
							{root.length === 0 ? (
								<p className="text-center py-4">
									<i className="fa-light fa-empty-set text-gray-500"></i>
								</p>
							) : (
								root.map((n) => (
									<div key={idKey(n.id)} className="border-b border-gray-100">
										<Item node={n} level={0} />
									</div>
								))
							)}
						</div>

						<div className="px-4 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
							<button
								type="button"
								className="px-3 py-1.5 rounded-lg text-sm text-slate-700 hover:bg-slate-200"
								onClick={() => {
									// revert staged to committed and close without saving
									setStagedSelected(selected)
									setStagedTitles(titles)
									setIsDirty(false)
									setOpen(false)
								}}
							>
								Cancel
							</button>
							<button
								type="button"
								className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
								onClick={async () => {
									await commitIfDirty()
									setOpen(false)
								}}
							>
								Save
							</button>
						</div>
					</div>,
					document.body,
				)}
		</div>
	)
}

export default MultiTreeSelect
